import React, { FC, ReactNode, useState, useEffect } from "react";
import classNames from "classnames";
import { eventCenter } from "@tarojs/taro";
import { View } from "@tarojs/components";
import useGenerateId from "@/hooks/useGenerateId";
import { pxTransform } from "@/utils";

import Group from "./Group";
import type { XHComponentCommonProps } from "../../types";
import Icon from "../Icon";

import "./index.scss";

type CompoundedComponent = React.ForwardRefExoticComponent<
  XHRadioProps & React.RefAttributes<HTMLInputElement>
> & {
  Group: typeof Group;
};

export type XHRadioProps = {
  onChange?: (check: boolean, value: string | number) => void;
  checked?: boolean;
  name?: string;
  label: string | ReactNode;
  size?: number;
  circle?: boolean;
  align?: "center" | "end";
  direction?: "rtl";
  justify?: "around" | "between";
  value?: string | number;
  id?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  fancy?: boolean;
  index?: number;
  /**
   * 抖音不支持属性选择器
   * @description 主要为抖音支持颜色
   */
  color?: string;
  group?: boolean;
  /**
   * 选中状态下打勾icon的颜色
   */
  iconColor?: string;
  dataTrackId?: string;
} & XHComponentCommonProps;

const XHRadio: FC<XHRadioProps> = props => {
  const {
    className,
    name,
    label,
    id,
    circle = true,
    onChange,
    align,
    direction,
    justify,
    checked,
    disabled,
    value,
    style,
    size = 0,
    fancy = false,
    index = 0,
    group = false,
    iconColor = "#FFFFFF",
    dataTrackId,
  } = props;

  const finalId = useGenerateId(id, "xh-radio-id");

  const [innerChecked, setInnerChecked] = useState(checked);

  const resetChecked = idx => {
    if (process.env.TARO_ENV !== "h5" && idx !== index) {
      setInnerChecked(false);
    }
  };

  useEffect(() => {
    if (process.env.TARO_ENV !== "h5") {
      eventCenter.on("radioChange", resetChecked);
    }

    return () => {
      eventCenter.off("radioChange", resetChecked);
    };
  }, []);

  const handleOnchange = e => {
    let finalchecked = checked || false;
    let value = "";

    if (process.env.TARO_ENV === "h5") {
      finalchecked = e.target.checked;
      value = e.target.value;
    } else {
      finalchecked = e.detail.value;

      value = e.detail.value;
    }

    onChange?.(finalchecked, value);
  };

  const handleFakeChange = () => {
    if (innerChecked && group) return;
    setInnerChecked(!innerChecked);
    onChange?.(!innerChecked, value || "");
  };

  const cls = classNames("xh-radio", className, align, direction, justify);
  if (process.env.TARO_ENV !== "h5") {
    return (
      <View
        // @ts-ignore
        dataTrackId={dataTrackId}
        onClick={handleFakeChange}
        className={cls}
        style={style}
      >
        <View
          className={classNames("xh-radio-input", {
            checked: innerChecked,
          })}
        />
        <span
          style={
            size > 0
              ? {
                  width: pxTransform(size),
                  height: pxTransform(size),
                }
              : {}
          }
          className={classNames("xh-radio-input-fake", circle && "circle", {
            fancy,
          })}
        >
          <Icon
            icon="duigou"
            className="xh-radio-input-fake-icon"
            color={iconColor}
            size={size ? size * 0.55 : 18}
          />
        </span>
        <span className="xh-radio-label">{label}</span>
      </View>
    );
  }

  return (
    <label
      htmlFor={finalId}
      className={cls}
      data-track-id={dataTrackId}
      style={style}
    >
      <input
        type="radio"
        id={finalId}
        name={name}
        className="xh-radio-input"
        onChange={handleOnchange}
        value={value}
        disabled={disabled}
        defaultChecked={checked}
      />
      <span
        style={
          size > 0
            ? {
                width: pxTransform(size),
                height: pxTransform(size),
              }
            : {}
        }
        className={classNames("xh-radio-input-fake", { circle, fancy })}
      >
        <Icon
          icon="duigou"
          className="xh-radio-input-fake-icon"
          color={iconColor}
          size={size ? size * 0.55 : 18}
        />
      </span>
      <span className="xh-radio-label">{label}</span>
    </label>
  );
};

const Radio = XHRadio as CompoundedComponent;
Radio.Group = Group;

export default Radio;
